// Brand Ambassadors gates (BA E3, principal spec 2026-11-02; WP-2d 2026-11-03).
//
// Every BA entry point in the app renders through one of TWO gates:
//
//   <BaSelfGate>   / useBaSelfEnabled()   — a Brand Ambassador's own screens:
//       My Portfolio (nav, route, and so Dana's page list), later Invitations
//       and My Event. Needs the `ba_platform` platform flag AND the company's
//       `brand_ambassadors` entitlement AND the permission `ba:profile:self`
//       (the `brand_ambassador` role, WP-2a).
//   <BaManageGate> / useBaManageEnabled() — the company side: the BA Roster,
//       review and "What clients see". Needs the flag AND the entitlement AND
//       `ba:profile:review` OR `services:booking:manage`.
//
// ⚠️ Presentation only: employee-service, commerce-service and
// workassignment-service enforce the same rules server-side (C20 as amended).
// Hiding here is never the check.
//
// STRICT, unlike hasProduct() and the fail-open flags elsewhere: BA is a paid
// product every company starts without, behind a platform flag seeded OFF. Not
// loaded yet, a failed load, an unknown flag key, a disabled entitlement row or
// a missing permission all mean "hidden". Hidden means nothing at all — never an
// error page.
import React from 'react';
import { useFeatureFlags } from '@/features/shared/featureFlags';
import { useHasAnyPermission, useHasPermission } from '@/rbac/useRbac';
import { useEntitlements } from './useEntitlements';

export const BA_PRODUCT = 'brand_ambassadors';
/** The master platform switch (WP-1). Not `brand_ambassadors`: that name is the entitlement. */
export const BA_PLATFORM_FLAG = 'ba_platform';
export const BA_SELF_PERMISSION = 'ba:profile:self';
export const BA_MANAGE_PERMISSIONS = ['ba:profile:review', 'services:booking:manage'];

export interface BaGateState { loaded: boolean; enabled: boolean }

/** The company's entitlement alone (strict). Used by both gates. */
export function useBaEnabled(): BaGateState {
  const { loaded, hasProductStrict } = useEntitlements();
  return { loaded, enabled: loaded && hasProductStrict(BA_PRODUCT) };
}

/** The `ba_platform` flag from /feature-flags/me: on only when the map loaded
 *  and says exactly `true`. Loading → not loaded; an error → loaded and off. */
/** BA gates re-read the flag map this often while mounted, so ba_platform
 *  switched OFF hides BA entry points within RL-5's bound (auth's 30 s poll +
 *  this) without a remount. */
export const BA_FLAG_REFRESH_MS = 30_000;

export function useBaPlatformFlag(): BaGateState {
  const { data, isSuccess, isError } = useFeatureFlags({ refreshMs: BA_FLAG_REFRESH_MS });
  const loaded = !!(isSuccess || isError);
  return { loaded, enabled: !!isSuccess && !!data && data[BA_PLATFORM_FLAG] === true };
}

function combine(flag: BaGateState, entitlement: BaGateState, permitted: boolean): BaGateState {
  const loaded = flag.loaded && entitlement.loaded;
  return { loaded, enabled: loaded && flag.enabled && entitlement.enabled && permitted };
}

export function useBaSelfEnabled(): BaGateState {
  const flag = useBaPlatformFlag();
  const entitlement = useBaEnabled();
  const permitted = useHasPermission(BA_SELF_PERMISSION);
  return combine(flag, entitlement, permitted);
}

export function useBaManageEnabled(): BaGateState {
  const flag = useBaPlatformFlag();
  const entitlement = useBaEnabled();
  const permitted = useHasAnyPermission(BA_MANAGE_PERMISSIONS);
  return combine(flag, entitlement, permitted);
}

export const BaSelfGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { enabled } = useBaSelfEnabled();
  return enabled ? <>{children}</> : null;
};

export const BaManageGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { enabled } = useBaManageEnabled();
  return enabled ? <>{children}</> : null;
};
